import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { useSelectionListKeyHandler } from "src/hooks/ticket/selection-list-key-handler/useSelectionListKeyHandler";
import { boldenMatchedSubstring } from "src/utils/Common";
import styles from "./RealestateFormField.module.css";
import "./GeneralInvoiceFormStyles.css";
import { ColorStyle } from "src/utils/Colors";
import { IRealestate } from "src/stores/krediflow/InvoiceTypes";
import { INVOICE_PROPERTY } from "src/stores/krediflow/InvoiceEnums";
import { AiFieldFilledHint } from "src/screens/creditors/creditor/details/form/fields/AiFieldFilledHint";
import { IRealestateData } from "src/stores/RealestateSearchStore";
import { ErpType } from "src/network/User";
import { DISPLAYED_MODAL_TYPE } from "src/stores/krediflow/AccountingStore";

const RealestateFormFieldBase = () => {
    const { t } = useTranslation();
    const { realestateSearchStore, invoiceStore, authStore, accountingStore } = useContext(RootStoreContext);

    const [displayRealestateList, setDisplayRealesteateList] = useState<boolean>(false);
    const [lastScrollPositionYRealestateItems, setLastSrollYPositionRealestateItems] = useState<number>(0);

    const realestateInputRef = useRef<HTMLInputElement>(null);

    const aiDetection = invoiceStore.currentInvoice?.aiDetection;
    const aiRealestateDetection = aiDetection?.results.realestateid;
    const aiRealestateOutcome = aiRealestateDetection?.outcome;

    // AI candidates shown at the top of the dropdown for ambiguous outcome.
    // Only rendered when the detection hasn't been resolved yet (acceptedsource null)
    // and the user hasn't started typing — once they type, the regular substring
    // filter takes over and AI rows drop out of the top section.
    const aiCandidatesInList: IRealestateData[] = useMemo(() => {
        if (!aiDetection || !aiRealestateDetection || aiRealestateOutcome !== "ambiguous") return [];
        // Already resolved (auto-applied or user-picked) — don't re-suggest.
        // Per-field state, like the creditor flow reads it.
        if ((aiRealestateDetection.acceptedsource ?? null) !== null) return [];
        const query = realestateSearchStore.currentRealestateSearchQuery ?? "";
        const isDefaultView = query === realestateSearchStore.selectedRealestateQueryString || query === "";
        if (!isDefaultView) return [];
        return realestateSearchStore.getRealestateDataByIds(
            aiRealestateDetection.candidates.map((candidate) => candidate.entityid)
        );
    }, [
        aiDetection,
        aiRealestateDetection,
        aiRealestateOutcome,
        realestateSearchStore,
        realestateSearchStore.currentRealestateSearchQuery,
        realestateSearchStore.selectedRealestateQueryString
    ]);

    const aiCandidateIdSet = useMemo(
        () => new Set(aiCandidatesInList.map((realestate) => realestate.id)),
        [aiCandidatesInList]
    );
    // Only reserve the indicator slot when the current list actually contains
    // AI candidates that need to be aligned against — no point burning
    // horizontal space on every row when there's nothing to line up.
    const showAiCandidateSlot = aiCandidateIdSet.size > 0;

    // AI candidates first, then the regular (filtered) list with AI ids removed.
    const mergedRealestateList: IRealestateData[] = useMemo(() => {
        if (aiCandidatesInList.length === 0) {
            return realestateSearchStore.filteredRealestateData;
        }
        return [
            ...aiCandidatesInList,
            ...realestateSearchStore.filteredRealestateData.filter((realestate) => !aiCandidateIdSet.has(realestate.id))
        ];
    }, [aiCandidatesInList, aiCandidateIdSet, realestateSearchStore.filteredRealestateData]);

    const resetSearchQuery = useCallback(() => {
        realestateSearchStore.setCurrentRealestateSearchQuery(
            realestateSearchStore.selectedRealestateQueryString ?? ""
        );
    }, [realestateSearchStore]);

    useEffect(() => {
        const handleClickOutsideRealestateInput = (event: any) => {
            if (
                displayRealestateList &&
                realestateInputRef.current &&
                !realestateInputRef.current.contains(event.target) &&
                // Ancestor-walk instead of one-level-parent class checks so any click
                // inside a row (icon, name line, number line, highlighted match span,
                // AI candidate slot) counts as "inside" regardless of DOM nesting depth.
                !(event.target instanceof Element && event.target.closest(".list-item.realestate"))
            ) {
                setDisplayRealesteateList(false);

                if (
                    invoiceStore.currentInvoice?.isRealestateSelected &&
                    realestateSearchStore.currentRealestateSearchQuery !==
                        realestateSearchStore.selectedRealestateQueryString
                ) {
                    resetSearchQuery();
                }
            }
        };

        document.addEventListener("mousedown", handleClickOutsideRealestateInput);

        return () => {
            document.removeEventListener("mousedown", handleClickOutsideRealestateInput);
        };
    }, [
        displayRealestateList,
        resetSearchQuery,
        realestateSearchStore.currentRealestateSearchQuery,
        realestateSearchStore.selectedRealestateQueryString,
        invoiceStore.currentInvoice?.isRealestateSelected
    ]);

    const updateInvoiceWithSelectedRealestate = useCallback(
        async (realestate: IRealestate) => {
            invoiceStore.updateInvoiceAndFormWithSelectedRealestate(realestate);
        },
        [invoiceStore]
    );

    const checkIfMandantHasChanged = useCallback(
        (newlySelectedRealestate: IRealestate) => {
            const consolidationIdOfNewlySelectedRealestate =
                newlySelectedRealestate.consolidation === true
                    ? newlySelectedRealestate.id
                    : newlySelectedRealestate.consolidationId;

            const currentlySelectedRealestate = invoiceStore.currentInvoice?.realestate;
            const consolidationIdOfCurrentlySelectedRealestate =
                currentlySelectedRealestate?.consolidation === true
                    ? currentlySelectedRealestate?.id
                    : currentlySelectedRealestate?.consolidationId;

            return consolidationIdOfCurrentlySelectedRealestate !== consolidationIdOfNewlySelectedRealestate;
        },
        [invoiceStore]
    );

    const updateAccountingWithSelectedRealestate = useCallback(
        async (realestate: IRealestate) => {
            await invoiceStore.currentInvoice?.loadPayoutAccountDataAndSetIfNeeded(realestate.id);
            invoiceStore.updateAccountingWithSelectedRealestate(realestate, true, 0);
        },
        [invoiceStore]
    );

    const updateInvoiceAndAccountingWithSelectedRealestate = useCallback(
        async (newlySelectedRealestate: IRealestate) => {
            const hasMandantChanged = checkIfMandantHasChanged(newlySelectedRealestate);

            const erpType = authStore.user?.erpType;

            if (erpType === ErpType.IT2) {
                if (hasMandantChanged && invoiceStore.currentInvoice?.isAccountingAccountSelected(0)) {
                    accountingStore.setRealestateThatTriggeredWarning(newlySelectedRealestate);
                    accountingStore.setDisplayedModalType(DISPLAYED_MODAL_TYPE.REALESTATE_CHANGED_WARNING);
                } else {
                    updateInvoiceWithSelectedRealestate(newlySelectedRealestate);

                    let realestateToUpdateAccountingWith = newlySelectedRealestate;

                    if (newlySelectedRealestate.consolidation !== true) {
                        realestateToUpdateAccountingWith =
                            realestateSearchStore.realestateData.find(
                                (realestate) => realestate.id === newlySelectedRealestate.consolidationId
                            ) ?? newlySelectedRealestate;
                    }

                    updateAccountingWithSelectedRealestate(realestateToUpdateAccountingWith);
                }

                const defaultAccountingRealestate =
                    await invoiceStore.currentInvoice?.getDefaultAccountingRealestateByRealestate(
                        newlySelectedRealestate
                    );

                if (defaultAccountingRealestate) {
                    await invoiceStore.currentInvoice?.loadPayoutAccountDataAndSetIfNeeded(
                        defaultAccountingRealestate.id
                    );
                }
            } else {
                updateInvoiceWithSelectedRealestate(newlySelectedRealestate);
            }
        },
        [
            updateInvoiceWithSelectedRealestate,
            accountingStore,
            authStore.user?.erpType,
            updateAccountingWithSelectedRealestate,
            checkIfMandantHasChanged,
            realestateSearchStore.realestateData,
            invoiceStore
        ]
    );

    const onSelectRealestateItem = useCallback(
        (activeListItemIndex: number) => {
            const selectedRealestateItem = mergedRealestateList[activeListItemIndex];
            if (!selectedRealestateItem) return;

            const realestate: IRealestate = {
                id: selectedRealestateItem.id,
                number: selectedRealestateItem.number,
                name: selectedRealestateItem.name,
                zip: selectedRealestateItem.zip,
                city: selectedRealestateItem.city,
                consolidation: selectedRealestateItem.consolidation,
                consolidationId: selectedRealestateItem.consolidationId
            };

            // User actively picked something — clear the auto-applied flag so the banner disappears.
            invoiceStore.currentInvoice?.setRealestateAutoAppliedByAi(false);
            updateInvoiceAndAccountingWithSelectedRealestate(realestate);
        },
        [mergedRealestateList, updateInvoiceAndAccountingWithSelectedRealestate, invoiceStore.currentInvoice]
    );

    const { onKeyDownFunction, setActiveListItemIndex, NO_LIST_ITEM_SELECTED } = useSelectionListKeyHandler({
        listName: "realestate",
        totalNumberOfDisplayedItems: invoiceStore.numberOfDisplayedItems,
        setIsListDisplayed: setDisplayRealesteateList,
        isListDisplayed: displayRealestateList,
        onSelectItem: onSelectRealestateItem,
        resetSearchQuery: resetSearchQuery
    });

    const resetAmountOfItemsDisplayedInRealestateList = useCallback(() => {
        setLastSrollYPositionRealestateItems(0);
        invoiceStore.setNumberOfDisplayedItems(invoiceStore.sliceSizeOfDisplayedItems);
    }, [invoiceStore]);

    const onChangeRealestateInput = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            resetAmountOfItemsDisplayedInRealestateList();

            const inputRealestate = e.target.value;
            realestateSearchStore.setCurrentRealestateSearchQuery(inputRealestate);
            // User edited the field — clear the auto-applied flag so the banner disappears.
            invoiceStore.currentInvoice?.setRealestateAutoAppliedByAi(false);
        },
        [realestateSearchStore, resetAmountOfItemsDisplayedInRealestateList, invoiceStore.currentInvoice]
    );

    const onFocusRealestateInput = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            if (invoiceStore.currentInvoice?.isRealestateSelected && realestateInputRef.current) {
                realestateInputRef.current.select();
            }

            resetAmountOfItemsDisplayedInRealestateList();
            setDisplayRealesteateList(true);
            setActiveListItemIndex(NO_LIST_ITEM_SELECTED);
        },
        [
            resetAmountOfItemsDisplayedInRealestateList,
            setActiveListItemIndex,
            NO_LIST_ITEM_SELECTED,
            invoiceStore.currentInvoice?.isRealestateSelected
        ]
    );

    const onClickRealestateItem = useCallback(
        (realestateData: IRealestateData) => async (e: any) => {
            setActiveListItemIndex(NO_LIST_ITEM_SELECTED);
            setDisplayRealesteateList(false);

            const newlySelectedRealestate: IRealestate = {
                id: realestateData.id,
                number: realestateData.number,
                name: realestateData.name,
                zip: realestateData.zip,
                city: realestateData.city,
                consolidation: realestateData.consolidation,
                consolidationId: realestateData.consolidationId
            };

            // User actively picked something — clear the auto-applied flag.
            invoiceStore.currentInvoice?.setRealestateAutoAppliedByAi(false);
            updateInvoiceAndAccountingWithSelectedRealestate(newlySelectedRealestate);

            invoiceStore.currentInvoice?.validateRealestateInvoiceDetails();
        },
        [
            setActiveListItemIndex,
            NO_LIST_ITEM_SELECTED,
            invoiceStore.currentInvoice,
            updateInvoiceAndAccountingWithSelectedRealestate
        ]
    );

    const onScrollRealestateList = useCallback(
        (e: React.BaseSyntheticEvent) => {
            const windowHeight = e.target.clientHeight;
            const pixelsFromTop = e.target.scrollTop;
            const totalHeight = e.target.scrollHeight;

            const is150PixelsFromBottom = totalHeight - pixelsFromTop < windowHeight + 150;
            const isScrollingDown = pixelsFromTop > lastScrollPositionYRealestateItems;

            const hasMoreResulsToDisplay = invoiceStore.numberOfDisplayedItems < mergedRealestateList.length;

            if (is150PixelsFromBottom && isScrollingDown && hasMoreResulsToDisplay) {
                invoiceStore.setNumberOfDisplayedItems(
                    invoiceStore.numberOfDisplayedItems + invoiceStore.sliceSizeOfDisplayedItems
                );
            }

            setLastSrollYPositionRealestateItems(pixelsFromTop);
        },
        [lastScrollPositionYRealestateItems, invoiceStore, mergedRealestateList.length]
    );

    const realestateSelectionList = (
        <ul
            id="realestate-dropdown-list"
            className={"dropdown-list realestate krediflow"}
            onScroll={onScrollRealestateList}
        >
            {mergedRealestateList.slice(0, invoiceStore.numberOfDisplayedItems).map((realestate, index) => {
                // Some backend `name` values already include "<zip> <city>" (esp. IT2), so
                // blindly appending zip/city would duplicate them. Only append when name
                // doesn't already contain the address suffix.
                const zipCitySuffix = `${realestate.zip ?? ""} ${realestate.city ?? ""}`.trim();
                const nameAlreadyContainsZipCity =
                    zipCitySuffix.length > 0 &&
                    realestate.name.toLowerCase().includes(zipCitySuffix.toLowerCase());
                const primaryLine =
                    zipCitySuffix.length === 0 || nameAlreadyContainsZipCity
                        ? realestate.name
                        : `${realestate.name}, ${zipCitySuffix}`;
                const numberLine = realestate.number ? `[${realestate.number}]` : undefined;

                const searchQuery = realestateSearchStore.currentRealestateSearchQuery ?? "";
                const formattedPrimary = boldenMatchedSubstring(searchQuery, primaryLine, "highlighted-text realestate");
                const formattedNumber = numberLine
                    ? boldenMatchedSubstring(searchQuery, numberLine, "highlighted-text realestate")
                    : undefined;

                const isAiCandidate = aiCandidateIdSet.has(realestate.id);

                return (
                    <li
                        key={index}
                        className="d-flex align-items-center list-item realestate"
                        onClick={onClickRealestateItem(realestate)}
                        id={`realestate-list-item-${index}`}
                    >
                        {showAiCandidateSlot && (
                            <span className={styles.AiCandidateIndicator} aria-hidden={!isAiCandidate}>
                                {isAiCandidate && (
                                    <UI.Icon icon={UI.SVGIcon.AiSparkle} size={22} color={ColorStyle("primary")} />
                                )}
                            </span>
                        )}
                        <UI.Icon
                            icon={realestate.consolidation ? UI.SVGIcon.Realestates : UI.SVGIcon.Realestate}
                            size={25}
                            circular
                            color={ColorStyle("grey")}
                            backgroundColor="#D2D5D9"
                            className="realestate-icon"
                        />
                        <div className={styles.RealestateItemText}>
                            <div
                                className={styles.RealestateItemPrimary}
                                title={primaryLine}
                                dangerouslySetInnerHTML={{ __html: formattedPrimary }}
                            />
                            {formattedNumber && (
                                <div
                                    className={styles.RealestateItemNumber}
                                    title={numberLine}
                                    dangerouslySetInnerHTML={{ __html: formattedNumber }}
                                />
                            )}
                        </div>
                    </li>
                );
            })}
        </ul>
    );

    const onBlurRealestateInput = useCallback(() => {
        invoiceStore.currentInvoice?.validateRealestateInvoiceDetails();
    }, [invoiceStore]);

    const readOnlyRealestateElement = (
        <div className="view-only-container mt-3 mt-lg-0">
            <div className="realestate bold-text mb-1">{t("screens.realestate.title")}</div>
            <div>
                <span className="text-with-number">{invoiceStore.currentInvoice?.realestate?.name}</span>
                <span>{`${
                    invoiceStore.currentInvoice?.realestate?.number
                        ? `[${invoiceStore.currentInvoice?.realestate?.number}]`
                        : ""
                }`}</span>
            </div>

            <div>
                {invoiceStore.currentInvoice?.realestate?.zip} {invoiceStore.currentInvoice?.realestate?.city}
            </div>
        </div>
    );

    return (
        <div className={styles.FieldWrapper}>
            {invoiceStore.isEditing ? (
                <>
                    <UI.Input
                        ref={realestateInputRef}
                        label={t("screens.realestate.title").toString()}
                        type="text"
                        as="textarea"
                        autoComplete="off"
                        className={styles.RealestateTextArea}
                        value={realestateSearchStore.currentRealestateSearchQuery ?? ""}
                        onChange={onChangeRealestateInput}
                        onFocus={onFocusRealestateInput}
                        onKeyDown={onKeyDownFunction}
                        onBlur={onBlurRealestateInput}
                        disabled={!invoiceStore.isEditing}
                        errorMsg={invoiceStore.currentInvoice?.errors.realestateRequired ?? undefined}
                    />
                    <AiFieldFilledHint field={INVOICE_PROPERTY.REALESTATE} />
                    {displayRealestateList ? realestateSelectionList : undefined}
                </>
            ) : (
                readOnlyRealestateElement
            )}
        </div>
    );
};

export const RealestateFormField = Sentry.withProfiler(observer(RealestateFormFieldBase));
